import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService, Appointment } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

interface DashboardStats {
  todaysAppointments: number;
  completedAppointments: number;
  pendingConfirmations: number;
  noShows: number;
}

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    todaysAppointments: 0,
    completedAppointments: 0,
    pendingConfirmations: 0,
    noShows: 0
  });
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [pendingWebsite, setPendingWebsite] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load upcoming appointments
      const appointmentsResponse = await apiService.getUpcomingAppointments();
      setUpcomingAppointments(appointmentsResponse.data.data || []);

      // Load all appointments to find pending website bookings (any date)
      const allResponse = await apiService.getAppointments();
      const all = (allResponse.data.data || []) as Appointment[];
      setPendingWebsite(all.filter(a => a.status === 'pending' && a.source === 'website')
        .sort((a, b) => `${a.appointment_date} ${a.start_time}`.localeCompare(`${b.appointment_date} ${b.start_time}`)));

      // Calculate stats from appointments
      const today = new Date().toISOString().split('T')[0];
      const todaysAppointments = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.appointment_date === today
      ).length || 0;

      const completedAppointments = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.status === 'completed'
      ).length || 0;

      const pendingConfirmations = all.filter(
        (apt: Appointment) => apt.status === 'pending'
      ).length;

      const noShows = appointmentsResponse.data.data?.filter(
        (apt: Appointment) => apt.status === 'no_show'
      ).length || 0;

      setStats({
        todaysAppointments,
        completedAppointments,
        pendingConfirmations,
        noShows
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'pending':
        return 'Pending';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-admin"></div>
      </div>
    );
  }

  const todayLong = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-xl shadow-md ring-1 ring-black/5 group">
        <img
          src="/images/admin-header-palma.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center scale-105 group-hover:scale-[1.08] transition-transform duration-[1500ms] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-admin/90 via-navy-admin/65 to-navy-admin/10" aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" aria-hidden />
        <div
          className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-mint/20 blur-3xl"
          aria-hidden
        />

        <div className="relative px-6 py-7 sm:px-8 sm:py-9">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block h-px w-8 bg-mint" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-mint">
              Palma de Mallorca
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight tracking-tight">
            {getGreeting()}{user?.firstName ? `, ${user.firstName}` : ''}
          </h1>
          <p className="text-sm text-white/85 mt-1.5 max-w-md">
            Hier ist dein Überblick — heute ist <span className="font-medium text-white">{todayLong}</span>.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">📅</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Today's Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todaysAppointments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">✅</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedAppointments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">⏳</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Confirmations</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingConfirmations}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="text-2xl">❌</div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">No Shows</p>
              <p className="text-2xl font-bold text-gray-900">{stats.noShows}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Website Bookings - prominent at top */}
      {pendingWebsite.length > 0 && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-purple-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-purple-900 flex items-center">
                <span className="text-2xl mr-2">🔔</span>
                Neue Buchungen über seadent.es
              </h2>
              <p className="text-sm text-purple-700">{pendingWebsite.length} ausstehend — bitte bestätigen oder ablehnen</p>
            </div>
            <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-semibold">
              {pendingWebsite.length}
            </span>
          </div>
          <div className="divide-y divide-purple-200">
            {pendingWebsite.map(a => (
              <Link key={a.id}
                to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                className="block px-6 py-4 hover:bg-purple-100"
                title="Im Kalender öffnen">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-purple-900">{a.patient_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-yellow-200 text-yellow-900 rounded-full">website</span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">
                      📅 {a.appointment_date} · ⏰ {a.start_time}–{a.end_time}
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      🦷 {a.procedure_name} · 👤 {a.provider_name} · 🏥 {a.operatory_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      📧 {a.patient_email} · 📞 {a.patient_phone}
                    </div>
                    {a.notes && (
                      <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded border border-purple-100 whitespace-pre-line">
                        {a.notes}
                      </div>
                    )}
                  </div>
                  <span className="text-purple-600 text-sm whitespace-nowrap">Open →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Appointments */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Upcoming Appointments</h2>
          <p className="text-sm text-gray-600">Next 7 days</p>
        </div>
        <div className="divide-y divide-gray-200">
          {upcomingAppointments.length === 0 ? (
            <div className="px-6 py-4 text-center text-gray-500">
              No upcoming appointments
            </div>
          ) : (
            upcomingAppointments.slice(0, 10).map((appointment) => (
              <Link
                key={appointment.id}
                to={`/admin/calendar?date=${appointment.appointment_date}&id=${appointment.id}`}
                className="block px-6 py-4 hover:bg-gray-50"
                title="Open in calendar"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {appointment.patient_name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{appointment.patient_name}</p>
                        <p className="text-sm text-gray-500">
                          {formatDate(appointment.appointment_date)} at {formatTime(appointment.start_time)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-sm text-gray-500">
                      <p>{appointment.provider_name}</p>
                      <p>{appointment.procedure_name}</p>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                      {getStatusLabel(appointment.status)}
                    </span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/calendar"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-2xl mr-3">📅</div>
            <div>
              <p className="text-sm font-medium text-gray-900">Calendar</p>
              <p className="text-xs text-gray-500">View and manage appointments</p>
            </div>
          </Link>
          
          <Link
            to="/admin/appointments"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-2xl mr-3">📋</div>
            <div>
              <p className="text-sm font-medium text-gray-900">Appointments</p>
              <p className="text-xs text-gray-500">Manage appointment list</p>
            </div>
          </Link>
          
          <Link
            to="/admin/providers"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="text-2xl mr-3">👨‍⚕️</div>
            <div>
              <p className="text-sm font-medium text-gray-900">Providers</p>
              <p className="text-xs text-gray-500">Manage doctors and staff</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 